import { useEffect, useState, useCallback } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Download, Loader2, Search, Mail } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const PAGE_SIZE = 50;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminWaitlistTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/waitlist", {
        params: { page, page_size: PAGE_SIZE, q: q || undefined },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Error al cargar la lista de espera");
    } finally {
      setLoading(false);
    }
  }, [page, q]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPage();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/waitlist/export`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rivones_waitlist.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo exportar el CSV");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5" data-testid="admin-waitlist-tab">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex w-9 h-9 rounded-xl bg-gradient-to-br from-terracotta to-amber-500 items-center justify-center text-white">
              <Mail className="w-4 h-4" />
            </span>
            <h2 className="font-heading font-bold text-xl text-agave">Lista de espera</h2>
          </div>
          <p className="text-sm text-neutral-500 mt-1">
            Visitantes que se suscribieron al banner de pre-lanzamiento.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right mr-2">
            <p className="text-xs uppercase tracking-wider text-neutral-400">Total</p>
            <p className="font-heading font-bold text-2xl text-[#111111] tabular-nums">
              {total.toLocaleString()}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exporting || total === 0}
            data-testid="waitlist-export-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            type="search"
            placeholder="Buscar por correo…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="waitlist-search-input"
          />
        </div>
        <Button type="submit" variant="secondary" data-testid="waitlist-search-btn">
          Buscar
        </Button>
      </form>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-neutral-500 text-sm">
            Aún no hay suscriptores.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Correo</TableHead>
                <TableHead className="hidden sm:table-cell">Origen</TableHead>
                <TableHead className="hidden md:table-cell">Fecha</TableHead>
                <TableHead className="hidden lg:table-cell">IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id || row.email} data-testid={`waitlist-row-${row.email}`}>
                  <TableCell className="font-medium">{row.email}</TableCell>
                  <TableCell className="hidden sm:table-cell text-neutral-500 text-sm">
                    {row.source || "—"}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-neutral-500 text-sm">
                    {row.created_at
                      ? format(new Date(row.created_at), "dd MMM yyyy HH:mm")
                      : "—"}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-neutral-400 text-xs font-mono">
                    {row.ip || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-neutral-500">
            Página {page} de {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
